import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../utils/ToastContext";
import { isPasswordValid } from "../../../utils/passwordValidation";
import { CheckEmailAvailability, UserRegister } from "../../../utils/apiCalls/auth";

// GS1 UDI carton prefix baked into the device barcode. The user only enters
// the two variable segments: (10) lot/batch code and (21) serial number.
export const UDI_STATIC_PREFIX = "(01)00860016217508";
// Same device naming convention as the Add Device flow (see AddDevice/useAddDevice.js):
// a "We-Be " prefix plus a 4-character code the user enters.
export const DEVICE_ID_PREFIX = "We-Be ";

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const NAME_RE = /^[A-Za-z0-9\s]+$/;
const USERNAME_RE = /^[A-Za-z0-9._-]+$/;
const UDI_LOT_CODE_RE = /^[A-Za-z0-9]{3}$/;
const UDI_SERIAL_RE = /^\d{7}$/;
const DEVICE_ID_SUFFIX_LENGTH = 4;

const initialValues = {
  name: "",
  username: "",
  email: "",
  udiLotCode: "",
  udiSerial: "",
  deviceIdSuffix: "",
  password: "",
  confirmPassword: "",
};

export default function useRegistration() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [formData, setFormData] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    // Email was verified for a previous value - any edit invalidates it.
    if (name === "email") {
      setIsEmailVerified(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (isVerifyingEmail) return;

    if (!formData.email) {
      setErrors((prev) => ({ ...prev, email: "This field is required" }));
      return;
    }

    if (!EMAIL_RE.test(formData.email)) {
      setErrors((prev) => ({ ...prev, email: "Enter a valid email" }));
      return;
    }

    setIsVerifyingEmail(true);
    try {
      const res = await CheckEmailAvailability(formData.email);
      const { statusCode, message } = res;

      if (statusCode === 200) {
        setIsEmailVerified(true);
        setErrors((prev) => ({ ...prev, email: "" }));
        showToast(message || "Email is available", "success");
      } else {
        setIsEmailVerified(false);
        setErrors((prev) => ({
          ...prev,
          email: message || "This email is already registered",
        }));
      }
    } catch (error) {
      setIsEmailVerified(false);
      showToast(error?.message, "error");
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const validate = () => {
    const errObj = {};

    if (!formData.name.trim()) {
      errObj.name = "This field is required";
    } else if (!NAME_RE.test(formData.name.trim())) {
      errObj.name = "Only alphabets, numbers and spaces are allowed";
    }

    if (!formData.username.trim()) {
      errObj.username = "This field is required";
    } else if (formData.username.trim().length < 3) {
      errObj.username = "Username must be at least 3 characters";
    } else if (!USERNAME_RE.test(formData.username.trim())) {
      errObj.username =
        "Only letters, numbers, dots, underscores and hyphens are allowed";
    }

    if (!formData.email) {
      errObj.email = "This field is required";
    } else if (!EMAIL_RE.test(formData.email)) {
      errObj.email = "Enter a valid email";
    } else if (!isEmailVerified) {
      errObj.email = "Please verify your email before continuing";
    }

    if (!formData.udiLotCode) {
      errObj.udiLotCode = "Required";
    } else if (!UDI_LOT_CODE_RE.test(formData.udiLotCode)) {
      errObj.udiLotCode = "Enter a 3-character alphanumeric code";
    }

    if (!formData.udiSerial) {
      errObj.udiSerial = "Required";
    } else if (!UDI_SERIAL_RE.test(formData.udiSerial)) {
      errObj.udiSerial = "Enter a 7-digit serial";
    }

    if (!formData.deviceIdSuffix.trim()) {
      errObj.deviceIdSuffix = "This field is required";
    } else if (formData.deviceIdSuffix.trim().length !== DEVICE_ID_SUFFIX_LENGTH) {
      errObj.deviceIdSuffix = "Enter a 4 digit code";
    }

    if (!formData.password) {
      errObj.password = "This field is required";
    } else if (!isPasswordValid(formData.password)) {
      errObj.password = "Password does not meet the requirements above";
    }

    if (!formData.confirmPassword) {
      errObj.confirmPassword = "This field is required";
    } else if (formData.confirmPassword !== formData.password) {
      errObj.confirmPassword = "Passwords do not match";
    }

    setErrors(errObj);
    return Object.keys(errObj).length === 0;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        userName: formData.username.trim(),
        email: formData.email,
        password: formData.password,
        deviceName: `${DEVICE_ID_PREFIX}${formData.deviceIdSuffix.trim()}`,
        deviceUdi: `${UDI_STATIC_PREFIX}(10)${formData.udiLotCode}(21)${formData.udiSerial}`,
      };

      const res = await UserRegister(payload);
      const { statusCode, message } = res;

      if (statusCode === 200) {
        showToast(
          message || "Registration successful. Please sign in to continue.",
          "success",
        );
        navigate("/");
      } else {
        showToast(message || "Registration failed. Please try again.", "error");
      }
    } catch (error) {
      showToast(error?.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToSignIn = () => navigate("/");

  return {
    formData,
    errors,
    isSubmitting,
    isVerifyingEmail,
    isEmailVerified,
    handleChange,
    handleVerifyEmail,
    handleSubmit,
    handleBackToSignIn,
  };
}
